import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import DashboardLayout from '../components/DashboardLayout';
import { Skeleton } from '../components/Skeleton';
import CopyButton from '../components/CopyButton';
import CountUp from '../components/CountUp';
import Icon from '../components/Icon';
import { formatDateTime } from '../lib/time';

export default function Referral() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.referral().then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout title={t('referral.title')} crumb={t('dashboard.title')} heading={false}>
        <Skeleton className="sk-block" style={{ marginTop: 20 }} />
      </DashboardLayout>
    );
  }

  const rewards = data?.rewards || { coupons: [], freeTime: [] };
  const hasRewards = rewards.coupons.length > 0 || rewards.freeTime.length > 0;

  return (
    <DashboardLayout title={t('referral.title')} crumb={t('dashboard.title')} heading={false}>
      <h1 className="page-title">{t('referral.title')}</h1>
      <p className="page-sub">{t('referral.subtitle')}</p>

      <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <div className="field" style={{ marginBottom: 18 }}>
          <label>{t('referral.your_code')}</label>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: '10px 14px' }}>
            <code style={{ fontSize: 16, letterSpacing: '0.1em', color: 'var(--accent)', fontFamily: 'ui-monospace, monospace' }}>{data.code}</code>
            <CopyButton text={data.code} />
          </div>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>{t('referral.your_link')}</label>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: '10px 14px', flexWrap: 'wrap' }}>
            <code style={{ fontSize: 12.5, color: 'var(--text-2)', wordBreak: 'break-all', fontFamily: 'ui-monospace, monospace' }}>{data.link}</code>
            <CopyButton text={data.link} />
          </div>
        </div>
      </div>

      <div className="stat-row">
        <div className="card stat">
          <div className="stat-label"><Icon name="users" size={13} /> {t('referral.signups')}</div>
          <div className="stat-value"><CountUp value={data.signups} /></div>
        </div>
        <div className="card stat">
          <div className="stat-label"><Icon name="check" size={13} /> {t('referral.converted')}</div>
          <div className="stat-value"><CountUp value={data.converted} /></div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ fontSize: '1rem', marginBottom: 6 }}>{t('referral.rewards_title')}</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 14 }}>{t('referral.how')}</p>
        {!hasRewards ? (
          <p style={{ color: 'var(--text-3)', fontSize: 13, margin: 0 }}>{t('referral.no_rewards')}</p>
        ) : (
          <div className="list">
            {rewards.freeTime.map((g, i) => (
              <div className="list-row" key={`ft${i}`} style={{ padding: '10px 0' }}>
                <span className="badge ok"><Icon name="gift" size={12} /> {t('referral.reward_free', { days: g.days })}</span>
                <div className="row-main" style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{g.note || ''}</div>
                <div className="row-time">{formatDateTime(g.at, i18n.resolvedLanguage)}</div>
              </div>
            ))}
            {rewards.coupons.map((c) => (
              <div className="list-row" key={c.code} style={{ padding: '10px 0' }}>
                <span className={`badge ${c.active ? 'ok' : ''}`}><Icon name="ticket" size={12} /> {t('referral.reward_coupon', { pct: c.discountPercent })}</span>
                <div className="row-main">
                  <code style={{ fontSize: 12.5, color: 'var(--accent)' }}>{c.code}</code>
                  {c.redeemedCount > 0 && <span style={{ color: 'var(--text-3)', fontSize: 11, marginLeft: 8 }}>({t('referral.reward_used')})</span>}
                </div>
                <CopyButton text={c.code} className="btn btn-ghost btn-sm" />
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
